'use client';

import React from 'react';
import styled from 'styled-components';

interface UptimeStatData {
  period: string;
  percentage: string;
  incidents: string;
  locked?: boolean;
}

interface UptimeStatsGridProps {
  stats: UptimeStatData[];
  className?: string;
  loading?: boolean;
}

const Container = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatItem = styled.div<{ $locked?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  opacity: ${({ $locked }) => ($locked ? 0.5 : 1)};
`;

const Period = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
`;

const Percentage = styled.div`
  font-size: 32px;
  font-weight: 600;
  color: #111827;
`;

const Incidents = styled.div`
  font-size: 14px;
  color: #6b7280;
`;

const LockedLink = styled.a`
  font-size: 14px;
  color: #10b981;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const CustomDateSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DatePickerButton = styled.button`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #d1d5db;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Skeleton = styled.div<{ $width?: string; $height?: string }>`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 4px;
  width: ${({ $width }) => $width || '100%'};
  height: ${({ $height }) => $height || '20px'};

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

/**
 * UptimeStatsGrid Component
 * 
 * Displays uptime statistics for different time periods
 */
export const UptimeStatsGrid: React.FC<UptimeStatsGridProps> = ({
  stats,
  className,
  loading = false,
}) => {
  return (
    <Container className={className}>
      <Grid>
        {loading ? (
          // Show skeleton loaders during refresh
          Array.from({ length: 3 }).map((_, index) => (
            <StatItem key={`skeleton-${index}`}>
              <Skeleton $width="80px" $height="16px" />
              <Skeleton $width="120px" $height="32px" />
              <Skeleton $width="150px" $height="16px" />
            </StatItem>
          ))
        ) : (
          stats.map((stat, index) => (
            <StatItem key={index} $locked={stat.locked}>
              {stat.period === 'Custom' ? (
                <CustomDateSection>
                  <DatePickerButton>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" strokeWidth={2} />
                      <line x1="16" y1="2" x2="16" y2="6" strokeWidth={2} strokeLinecap="round" />
                      <line x1="8" y1="2" x2="8" y2="6" strokeWidth={2} strokeLinecap="round" />
                      <line x1="3" y1="10" x2="21" y2="10" strokeWidth={2} />
                    </svg>
                    Pick a date range
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ marginLeft: 'auto' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </DatePickerButton>
                  <Percentage>{stat.percentage}</Percentage>
                  <Incidents>{stat.incidents}</Incidents>
                </CustomDateSection>
              ) : (
                <>
                  <Period>{stat.period}</Period>
                  <Percentage>{stat.percentage}</Percentage>
                  {stat.locked ? (
                    <LockedLink href="#">Unlock with paid plans</LockedLink>
                  ) : (
                    <Incidents>{stat.incidents}</Incidents>
                  )}
                </>
              )}
            </StatItem>
          ))
        )}
      </Grid>
    </Container>
  );
};

export default UptimeStatsGrid;

